import { createSelector } from '@reduxjs/toolkit';
import { format, isBefore } from 'date-fns';
import { PaymentType } from '@acorn-portal/shared/src/services/cancellation-api';
import type { RootState } from '@acorn-portal/shared/src/store';

/** Base selector: the cancellation slice from root state. Read-only; no unit tests required. */
export const selectCancellation = (state: RootState) => state.cancellation;

/** Rate data from the last propose/amend response. Read-only; no unit tests required. */
export const selectRateData = createSelector(selectCancellation, (c) => c.cancellationRatingResponse?.rateData);

/** Flags derived from cancellation date description and staleness. */
export const selectCancellationFlags = createSelector(selectCancellation, (c) => {
	const isNow = c.cancellationDate.description === 'NOW';
	const isLater = c.cancellationDate.description === 'LATER';
	const isoDate = c.cancellationRatingCommand?.cancellationDate;
	const isStale = isLater && !!isoDate && isBefore(new Date(isoDate), new Date());
	return { isNow, isLater, isStale };
});

/** Refund vs charge eligibility from rate data. */
export const selectPaymentEligibility = createSelector(selectRateData, (rateData) => ({
	eligibleForRefund: rateData?.paymentType === PaymentType.Refund,
	eligibleForClearBalance: rateData?.paymentType === PaymentType.Charge,
}));

/** Cancellation date formatted for display (e.g. "19th February 2024"). Format only; covered by selector tests. */
export const selectCancellationDateFormatted = createSelector(selectCancellation, (c) => {
	try {
		return format(new Date(c.cancellationDate.date), 'do MMMM yyyy');
	} catch {
		return '';
	}
});

/** Cancellation time formatted for display (e.g. "14:30"). Format only; covered by selector tests. */
export const selectCancellationTimeFormatted = createSelector(selectCancellation, (c) => {
	try {
		return format(new Date(c.cancellationDate.date), 'HH:mm');
	} catch {
		return '';
	}
});

/** Policy holder email from full policy, for display on success page. */
export const selectPolicyHolderEmail = (state: RootState) => state.fullPolicy?.policyholder?.email ?? '';
